import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import './LoginPage.css';
import { supabase } from './supabaseClient';
import bcrypt from 'bcryptjs';

const SignUpPage = () => {
  const navigate = useNavigate();
  const [signUpData, setSignUpData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [signUpError, setSignUpError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSignUpData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!signUpData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!signUpData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!signUpData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signUpData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!signUpData.password) {
      newErrors.password = 'Password is required';
    } else if (signUpData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!signUpData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (signUpData.password !== signUpData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setSignUpError('');
    
    if (validateForm()) {
      setIsLoading(true);
      
      try {
        // Check if email already exists
        const { data: existingUser } = await supabase
          .from('users')
          .select('email')
          .eq('email', signUpData.email.toLowerCase())
          .single();

        if (existingUser) {
          setSignUpError('This email is already registered. Please use a different email or login.');
          setIsLoading(false);
          return;
        }

        // Hash the password
        const saltRounds = 10;
        const passwordHash = await bcrypt.hash(signUpData.password, saltRounds);

        // Combine first and last name
        const fullName = `${signUpData.firstName} ${signUpData.lastName}`;

        // Insert user into Supabase
        const { data, error } = await supabase
          .from('users')
          .insert([
            {
              name: fullName,
              email: signUpData.email.toLowerCase(),
              password_hash: passwordHash,
              status: 'active'
            }
          ])
          .select()
          .single();

        if (error) {
          throw error;
        }

        // Store user info in localStorage (without password)
        const userInfo = {
          id: data.id,
          name: data.name,
          email: data.email
        };
        localStorage.setItem('user', JSON.stringify(userInfo));
        localStorage.setItem('isSignedIn', 'true');
        
        // Dispatch custom event to update header
        window.dispatchEvent(new Event('authStatusChange'));
        
        // Navigate to dashboard after successful sign up
        navigate('/dashboard');
      } catch (error) {
        console.error('Sign up error:', error);
        setSignUpError(error.message || 'Failed to create account. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="login-page">
      <Header />
      
      <section className="login-section">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <h1 className="login-title">Create Account</h1>
              <p className="login-subtitle">
                Sign up to start booking your flights.
              </p>
            </div>

            <form onSubmit={handleSignUp} className="login-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName" className="form-label">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={signUpData.firstName}
                    onChange={handleInputChange}
                    placeholder="John"
                    className={`form-input ${errors.firstName ? 'error' : ''}`}
                    required
                  />
                  {errors.firstName && (
                    <span className="error-message">{errors.firstName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName" className="form-label">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={signUpData.lastName}
                    onChange={handleInputChange}
                    placeholder="Doe"
                    className={`form-input ${errors.lastName ? 'error' : ''}`}
                    required
                  />
                  {errors.lastName && (
                    <span className="error-message">{errors.lastName}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={signUpData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@example.com"
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  required
                />
                {errors.email && (
                  <span className="error-message">{errors.email}</span>
                )}
              </div>
          
              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={signUpData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  className={`form-input ${errors.password ? 'error' : ''}`}
                  required
                />
                {errors.password && (
                  <span className="error-message">{errors.password}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={signUpData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm your password"
                  className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                  required
                />
                {errors.confirmPassword && (
                  <span className="error-message">{errors.confirmPassword}</span>
                )}
              </div>

              {signUpError && (
                <div className="error-message" style={{ marginBottom: '1rem', textAlign: 'center' }}>
                  {signUpError}
                </div>
              )}

              <button type="submit" className="login-button" disabled={isLoading}>
                {isLoading ? 'Creating Account...' : 'Sign Up'}
              </button>
            </form>

            <div className="login-footer">
              <div className="signup-section">
                <p className="signup-text">
                  Already have an account?{' '}
                  <Link to="/login" className="signup-link">
                    Login
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SignUpPage;
